// Global Instructions Rule Applied!
//
// Operations > Tranches grid page — cfg-driven reporting grid (clone of the
// Asset Transactions page). Tranches are read-only engine records created at
// booking, so there is no add button; rows link to the Tranche form.
import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '../../state_manager/store.ts'
import { getReportingGridColumnsForReportId } from '../../state_manager/utils/reportingGridColumns.ts'
import { useReportingGridColumnsWithPreferences } from '../../state_manager/hooks/useReportingGridColumnsWithPreferences.ts'
import { getAddColumnsCatalogForReport } from '../../state_manager/utils/reportingColumnCatalog.ts'
import type { ViewConfigColumn } from '../../core/components/ViewConfig/types.ts'
import { useTranchesData } from './hooks/useTranchesData.ts'
import { useTranchesReportId } from '../../state_manager/hooks/useTranchesReportId.ts'
import { TranchesTable } from './components/TranchesTable.tsx'
import ViewToolbar from '../../core/components/ViewToolbar/index.tsx'
import { ViewConfig } from '../../core/components/ViewConfig/index.ts'
import { fetchAllTranchesForExport } from '../shared/table_grid_export/fetchAllFilteredForExport.ts'
import { useReportingGridExport } from '../shared/table_grid_export/useReportingGridExport.ts'

export default function TranchesPage() {
  const reportingConfigs = useSelector((state: RootState) => state.globalConfigs?.reporting_configs ?? [])
  const reportingColumnDefaults = useSelector(
    (state: RootState) => state.globalConfigs?.reporting_column_defaults ?? []
  )
  const reportingColumns = useSelector((state: RootState) => state.globalConfigs?.reporting_columns ?? [])

  const reportId = useTranchesReportId()

  const {
    data,
    dataLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    hasMore,
    currentPage,
    loadingMore,
    filterOptions,
    filterOptionsLoading,
    handleFetch,
    handleSetFilters,
    handleSetSorting,
    handleResetFilters,
    handleLoadMore,
    defaultTableFilters,
    defaultTableSorting,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault
  } = useTranchesData()

  const gridColumns = useReportingGridColumnsWithPreferences(reportId, preferenceRow)

  const fetchAllForExport = useCallback(
    (token: string | null) => fetchAllTranchesForExport(activeSorting, activeFilters, token),
    [activeSorting, activeFilters]
  )
  const { exportLoading, handleExport } = useReportingGridExport({
    title: 'Tranches',
    gridColumns,
    fetchAll: fetchAllForExport
  })

  const addColumnsCatalog = useMemo(
    () =>
      getAddColumnsCatalogForReport(
        reportId,
        {
          reporting_columns: reportingColumns,
          reporting_column_defaults: reportingColumnDefaults
        },
        { primaryModuleKey: 'tranches' }
      ),
    [reportId, reportingColumns, reportingColumnDefaults]
  )

  const catalogFieldNameOrder = useMemo(
    () => [...addColumnsCatalog.deal, ...addColumnsCatalog.related].map((c) => c.valueKey),
    [addColumnsCatalog]
  )

  const defaultTableColumnKeys = useMemo(() => {
    if (!reportId) return null
    const rows = getReportingGridColumnsForReportId(reportId, {
      reporting_configs: reportingConfigs,
      reporting_column_defaults: reportingColumnDefaults,
      reporting_columns: reportingColumns
    })
    return rows.map((r) => r.valueKey)
  }, [reportId, reportingConfigs, reportingColumnDefaults, reportingColumns])

  const columnByValueKey = useMemo(() => {
    const m = new Map<string, ViewConfigColumn>()
    for (const c of addColumnsCatalog.deal) m.set(c.valueKey, c)
    for (const c of addColumnsCatalog.related) m.set(c.valueKey, c)
    for (const c of gridColumns) {
      if (!m.has(c.valueKey)) m.set(c.valueKey, c)
    }
    return m
  }, [addColumnsCatalog, gridColumns])

  const resolveViewConfigColumn = useCallback(
    (valueKey: string) => columnByValueKey.get(valueKey),
    [columnByValueKey]
  )

  const columnLayout = useMemo(
    () => ({
      onPersistFieldOrder: persistColumnFieldOrder,
      onResetToDefaults: resetColumnLayoutToDefault,
      resolveColumn: resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    }),
    [
      persistColumnFieldOrder,
      resetColumnLayoutToDefault,
      resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    ]
  )

  return (
    <div className='h-full overflow-hidden'>
      <div className='w-full h-full flex flex-col bg-white px-2 sm:px-4 pb-2 sm:pb-4 overflow-hidden'>
        <ViewToolbar
          moduleKey='tranches'
          searchEnabled={false}
          addButtonEnabled={false}
          activeFilters={activeFilters}
          onRefreshData={(filters) => handleFetch(activeSorting, Array.isArray(filters) ? filters : activeFilters)}
          onFilterChange={(filters) => handleSetFilters(Array.isArray(filters) ? filters : [])}
          defaultFilters={defaultTableFilters}
          onResetFilters={handleResetFilters}
          exportEnabled
          onExport={handleExport}
          exportLoading={exportLoading}
          beforeAddSlot={
            <ViewConfig
              moduleTitle='Tranches'
              columns={gridColumns}
              filterOptions={filterOptions}
              filterOptionsLoading={filterOptionsLoading}
              addColumnsCatalog={addColumnsCatalog}
              addColumnsTabLabels={{ primary: 'Tranche', related: 'Related' }}
              columnLayout={columnLayout}
              filteringConfig={{
                currentFilters: activeFilters,
                defaultFilters: defaultTableFilters,
                onApplyFilters: async (filters) => {
                  handleSetFilters(filters)
                },
                onResetToDefaults: async () => {
                  handleSetFilters(defaultTableFilters)
                }
              }}
              sortingConfig={{
                currentSorting: activeSorting,
                defaultSorting: defaultTableSorting,
                onApplySorting: async (sorting) => {
                  handleSetSorting(sorting)
                },
                onResetToDefaults: async () => {
                  handleSetSorting(defaultTableSorting)
                }
              }}
            />
          }
        />

        <div className='flex-1 min-h-0 h-full flex flex-col'>
          <TranchesTable
            data={data}
            loading={dataLoading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            currentPage={currentPage}
            gridColumns={gridColumns}
            activeFilters={activeFilters}
            activeSorting={activeSorting}
            filterOptions={filterOptions}
            filterOptionsLoading={filterOptionsLoading}
            onLoadMore={handleLoadMore}
            onFiltersChange={(filters) => {
              const filterArray = Object.entries(filters).map(([key, value]) => ({ key, value }))
              handleSetFilters(filterArray)
            }}
            onSortChange={(sortConfig: any) => {
              if (sortConfig) {
                handleSetSorting([{ key: sortConfig.column, direction: sortConfig.direction }])
              } else {
                handleSetSorting([])
              }
            }}
          />
        </div>
      </div>
    </div>
  )
}
